<template>
  <form class="filter">
    <CaretButton
      :class="buttonClasses"
      :aria-expanded="expanded"
      @click.native.prevent="toggleExpanded"
    >
      <span class="filter__active-marker" v-if="numberOfActiveFilters > 0">
        <span class="visually-hidden">
          {{ str('cubeFilter.activeFilters') }}
        </span>
        {{ numberOfActiveFilters }}
      </span>
      {{ str('cubeFilter.filter') }}
    </CaretButton>
    <transition name="expand">
      <div class="filter__body" v-if="expanded">
        <div class="filter__options stack">
          <fieldset class="stack">
            <div class="filter-toggle stack-horizontal">
              <div class="filter-toggle__input">
                <JsToggle
                  :color="primaryColor"
                  :sync="true"
                  :value="showOnlyProducers"
                  @change="toggleShowOnlyProducers"
                  :height="30"
                  aria-labelledby="producer-label"
                />
              </div>
              <span
                class="filter-toggle__label"
                id="producer-label"
                @click="toggleShowOnlyProducers"
              >
                {{ str('cubeFilter.onlyProducers') }}
              </span>
            </div>
            <div
              class="filter-toggle stack-horizontal"
              v-if="showOnlyProducers"
            >
              <div class="filter-toggle__input">
                <JsToggle
                  :color="primaryColor"
                  :sync="true"
                  :value="showOnlyProducersWithCapacity"
                  @change="toggleShowOnlyProducersWithCapacity"
                  :height="30"
                  aria-labelledby="producer-capacity-label"
                />
              </div>
              <span
                class="filter-toggle__label"
                id="producer-capacity-label"
                @click="toggleShowOnlyProducersWithCapacity"
              >
                {{ str('cubeFilter.onlyProducersWithCapacity') }}
              </span>
            </div>
          </fieldset>
          <fieldset v-if="showOnlyProducers">
            <label id="energy-type-label" class="filter__label">
              {{ str('cubeFilter.energyType') }}
            </label>
            <StyledSelect label="energy-type-label" v-model="energyType">
              <option value="all">
                {{ str('cubeFilter.allEnergyTypes') }}
              </option>
              <option
                v-for="(name, value) in productionTypes"
                :key="value"
                :value="value"
              >
                {{ name }}
              </option>
            </StyledSelect>
          </fieldset>
          <fieldset v-if="showOnlyProducers">
            <legend id="price-label" class="filter__label">
              {{ str('cubeFilter.price') }}
            </legend>
            <div class="stack-horizontal">
              <label class="filter-range__label" for="price-min">
                <span>
                  {{ str('cubeFilter.from') }}
                </span>
                <span>
                  {{ priceMin }}
                  <span class="filter__unit">
                    {{ str('cubeFilter.priceUnit') }}
                  </span>
                </span>
              </label>
              <input
                class="filter-range"
                type="range"
                min="0"
                max="10"
                v-model="priceMin"
                id="price-min"
              />
            </div>
            <div class="stack-horizontal">
              <label class="filter-range__label" for="price-max">
                <span>
                  {{ str('cubeFilter.to') }}
                </span>
                <span>
                  {{ priceMax }}
                  <span class="filter__unit">
                    {{ str('cubeFilter.priceUnit') }}
                  </span>
                </span>
              </label>
              <input
                class="filter-range"
                type="range"
                min="0"
                max="10"
                v-model="priceMax"
                id="price-max"
              />
            </div>
          </fieldset>
          <div class="filter__actions stack-horizontal">
            <button class="button button--link" @click.prevent="resetFilter">
              {{ str('cubeFilter.reset') }}
            </button>
            <div class="filter__results">
              {{ numberOfResults }} {{ str('cubeFilter.results') }}
            </div>
          </div>
        </div>
      </div>
    </transition>
  </form>
</template>

<script>
  import { defineComponent, ref, watch, computed } from '@vue/composition-api'
  import CaretButton from '@/components/efriends/CaretButton'
  import Check from '@/components/icons/Check'

  import StyledSelect from '@/components/StyledSelect'
  import { ToggleButton as JsToggle } from 'vue-js-toggle-button'
  import { useToggle } from '@/composables/useToggle'

  export default defineComponent({
    components: { CaretButton, StyledSelect, JsToggle, Check },
    props: {
      buttonClasses: {
        type: String,
        default: 'button button--secondary',
      },
    },
    setup(props, { root }) {
      const store = root.$store
      const primaryColor = ref(process.env.VUE_APP_SCSS_COLOR_COMMUNITY)
      const [expanded, toggleExpanded] = useToggle()
      const numberOfActiveFilters = computed(
        () => store.getters.numberOfActiveFilters
      )

      const showOnlyProducers = computed(
        () => store.state.cubeFilter.showOnlyProducers
      )
      const toggleShowOnlyProducers = () =>
        store.commit('toggleShowOnlyProducers')

      const showOnlyProducersWithCapacity = computed(
        () => store.state.cubeFilter.showOnlyProducersWithCapacity
      )
      const toggleShowOnlyProducersWithCapacity = () =>
        store.commit('toggleShowOnlyProducersWithCapacity')

      const energyType = computed({
        get: () => store.state.cubeFilter.energyType,
        set: (type) => store.commit('setEnergyType', type),
      })
      const priceMin = computed({
        get: () => store.state.cubeFilter.priceMin,
        set: (price) => store.commit('setPriceMin', price),
      })
      const priceMax = computed({
        get: () => store.state.cubeFilter.priceMax,
        set: (price) => store.commit('setPriceMax', price),
      })

      const numberOfResults = computed(
        () => store.getters.allCubesByDistanceFrom({ limit: Infinity }).length
      )

      const resetFilter = () => store.commit('resetFilter')

      const productionTypes = computed(() => {
        const types = store.state.strings.productionTypes
        return (
          Object.keys(types)
            // Filter Non-Producers
            .filter((key) => key !== 'none')
            .reduce((obj, key) => {
              obj[key] = types[key]
              return obj
            }, {})
        )
      })

      // Ensure price values never overlap and create a negative range
      watch(priceMin, (newValue) => {
        priceMax.value = Math.max(newValue, priceMax.value)
      })

      watch(priceMax, (newValue) => {
        priceMin.value = Math.min(newValue, priceMin.value)
      })

      return {
        expanded,
        toggleExpanded,
        primaryColor,
        showOnlyProducers,
        toggleShowOnlyProducers,
        showOnlyProducersWithCapacity,
        toggleShowOnlyProducersWithCapacity,
        energyType,
        priceMin,
        priceMax,
        numberOfResults,
        resetFilter,
        productionTypes,
        numberOfActiveFilters,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .stack {
    display: flex;
    flex-direction: column;

    > * + * {
      margin-top: spacer(1);
    }
  }

  .stack-horizontal {
    display: flex;
    align-items: center;

    > * + * {
      margin-left: spacer(1);
    }
  }

  .filter {
    display: flex;
    flex-direction: column;
    overflow: visible;
  }

  .filter__active-marker {
    display: inline-block;
    width: 1.5em;
    height: 1.5em;
    margin-right: 0.2em;
    background-color: $grey-darker;
    border-radius: 50%;
    margin-right: 0.2em;
    font-size: 0.9em;
    color: $white;
  }

  .filter__body {
    position: relative;
    max-height: 30rem;
    width: 100%;
    background-color: ghostwhite;
    overflow: hidden;
    z-index: 1;
  }

  .expand-enter-active,
  .expand-leave-active {
    transition: max-height 0.3s ease-out;
  }
  .expand-enter,
  .expand-leave-to {
    max-height: 0;
  }

  .filter__options {
    padding: spacer(3) spacer(1) spacer(2);

    > * + * {
      margin-top: spacer(4);
    }
  }

  .filter__label {
    display: block;
    margin-bottom: 0;
    font-size: 0.9em;
    color: $grey-dark;
  }

  .filter-range {
    margin-top: 0;
    margin-bottom: 0;
  }

  .filter-range__label {
    display: flex;
    min-width: 11ch;
    justify-content: space-between;
    white-space: nowrap;
  }

  .filter__unit {
    font-size: 0.8em;
    align-self: flex-end;
    color: $grey-dark;
  }

  .filter__actions {
    justify-content: space-between;

    > button:first-child {
      padding-left: 0;
    }
  }

  .filter-toggle__label {
    cursor: pointer;
  }

  .filter__results {
    white-space: nowrap;
  }
</style>
